/**
 * web/screens/game.ts
 * DOM host for the platformer scene. Today this serves W1 and W3;
 * W4 and W5 plug into the same route once their session helpers land.
 *
 * Owns orientation/UI state + scene lifecycle. Per-world session
 * dispatch is delegated to {@link buildPlatformerSessionBundle}; the
 * engine loop is delegated to {@link PlayScene} + the shared
 * {@link runGameRenderFrame} so render output matches the iOS bundle
 * exactly.
 *
 * @module web/screens/game
 */

/// <reference lib="dom" />

import type { Route } from '../router';
import { injectStyles } from '../styles';
import { loadGameAssets, setTiledMapForLevel } from '../assets';
import { mountHud } from '../hud';
import { mountTouchControls } from '../touchControls';
import { attachKeyboardListeners, clearAllHeld } from '../keyboard';
import {
  showPauseOverlay,
  showLevelCompleteOverlay,
  showGameOverOverlay,
  showNovaBark,
  type OverlayHandle,
} from '../overlays';

import { runGameRenderFrame } from '@src/game/rendering/gameRenderFrame';
import { getRenderAssets } from '@src/game/rendering/renderAssetStore';
import { PlayScene } from '@src/game/scenes/PlayScene';
import { buildPlatformerSessionBundle } from '@src/game/levels/platformerSessionBundle';
import { isFinaleLevelId } from '@src/game/levels/level5Session';
import { startEndingCutscene } from './gameEnding';
import { gameState } from '@src/game/engine/gameState';
import { clearAllInputState } from '@src/game/input/inputState';
import { getUserSetting } from '@src/game/services/userSettings';
import {
  registerGameEventHandlers,
  clearGameEventHandlers,
} from '@src/game/engine/gameEventDispatch';
import { getNovaBark } from '@src/game/services/narrativeManager';
import { playMusic, playSfx, stopMusic } from '@src/game/services/audioManager';
import { getMusicForLevel } from '@src/game/data/levelMusic';
import * as SaveManager from '@src/game/services/saveManager';
import { setActiveCharacter } from '@src/game/data/activeCharacter';
import { startIntroCutscene } from './gameIntro';
import {
  VIEWPORT_WIDTH,
  VIEWPORT_HEIGHT,
} from '@src/constants/gameConstants';

const POLL_MS = 200;

/** Build the in-game pause button (top-right). */
function buildPauseButton(onPause: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'sf-game-pause-btn';
  btn.setAttribute('aria-label', 'Pause game');
  btn.textContent = '⏸';
  btn.addEventListener('click', onPause);
  return btn;
}

/** Game-screen route factory. */
export const gameScreen: Route = (router, params) => {
  injectStyles();
  const bundle = buildPlatformerSessionBundle(params.get('level') ?? undefined);
  const { session } = bundle;

  const root = document.createElement('section');
  root.className = 'sf-game-root';
  root.dataset['screen'] = 'game';

  const canvas = document.createElement('canvas');
  canvas.className = 'sf-game-canvas';
  canvas.width = VIEWPORT_WIDTH;
  canvas.height = VIEWPORT_HEIGHT;
  root.append(canvas);
  const ctx = canvas.getContext('2d');
  if (ctx) ctx.imageSmoothingEnabled = false;

  // Push the active level to the renderer immediately so the first frame
  // paints the correct tile map even while sprite images are still loading.
  setTiledMapForLevel(session.id);

  const scene = new PlayScene();
  bundle.initScene(scene, { canSkip: false, skipCheckpointRestore: false });

  // Reconcile active character + skip-cutscene on replay (fire-and-forget).
  let cancelledSaveReconcile = false;
  void (async (): Promise<void> => {
    try {
      const save = await SaveManager.load();
      if (cancelledSaveReconcile) return;
      if (save?.selectedCharacter) setActiveCharacter(save.selectedCharacter);
      // DECISION: Only re-init when the bundle actually honours canSkip
      // (currently W1's finale cutscene). Re-initialising a W3 scene
      // here would discard the player's in-progress run with no
      // behavioural change, because initWorld3Scene ignores canSkip.
      if (
        bundle.honoursCanSkip
        && save?.levels[session.id]?.completed
      ) {
        bundle.initScene(scene, { canSkip: true, skipCheckpointRestore: false });
      }
    } catch {
      /* default canSkip=false */
    }
  })();

  // ── DOM overlays + mounts ────────────────────────────────────────────────
  const hud = mountHud(root);
  const showTouch = getUserSetting('showTouchControls');
  const touch = showTouch ? mountTouchControls(root) : null;
  const detachKeyboard = attachKeyboardListeners();

  let paused = false;
  let levelComplete = false;
  let gameOver = false;
  let overlay: OverlayHandle | null = null;

  const closeOverlay = (): void => {
    if (overlay) { overlay.destroy(); overlay = null; }
  };

  const setActiveGate = (): boolean => !paused && !levelComplete && !gameOver;

  const handleResume = (): void => {
    clearAllHeld();
    paused = false;
    closeOverlay();
    hud.setActive(true);
  };

  const handleRestart = (): void => {
    clearAllHeld();
    const canSkip = gameState.levelComplete || false;
    bundle.initScene(scene, { canSkip, skipCheckpointRestore: true });
    paused = false;
    levelComplete = false;
    gameOver = false;
    closeOverlay();
    hud.setActive(true);
  };

  const handleQuit = (): void => {
    router.back('/world-map');
  };

  const handlePause = (): void => {
    if (!setActiveGate()) return;
    clearAllHeld();
    paused = true;
    hud.setActive(false);
    closeOverlay();
    overlay = showPauseOverlay(root, {
      onResume: handleResume,
      onRestart: handleRestart,
      onQuit: handleQuit,
    });
  };

  const handleNextLevel = (): void => {
    const next = bundle.getNext(session.id);
    if (next === null) return;
    // DECISION: router.navigate destroys + remounts the screen, which re-runs
    // the full level-setup pipeline (setTiledMapForLevel, bundle.initScene)
    // in the new instance. Doing it here too is duplicate work that is
    // immediately discarded by the destroy().
    router.navigate('/game', { level: next });
  };

  root.append(buildPauseButton(handlePause));

  // ── Narrative + audio wiring ─────────────────────────────────────────────
  registerGameEventHandlers({
    onLand: () => { playSfx('land'); },
    onHurt: () => { playSfx('nova-hurt'); },
    onSeedCollected: () => { playSfx('seed-collect'); },
    onHeartFragmentCollected: () => { playSfx('heart-collect'); },
    onDeath: () => { playSfx('nova-death'); },
    onCheckpointActivated: () => {
      playSfx('checkpoint');
      showNovaBark(root, getNovaBark('checkpointActivate'));
    },
    onShardCollected: () => {
      playSfx('shard-collect');
      showNovaBark(root, getNovaBark('shardCollected'));
    },
    onLowHealth: () => {
      showNovaBark(root, getNovaBark('lowHealth'));
    },
  });

  // Music is a no-op for worlds with no committed track — see levelMusic.ts.
  const track = getMusicForLevel(session.id);
  if (track !== null) playMusic(track);

  // ── Intro cutscene (first-run only) ──────────────────────────────────────
  const intro = startIntroCutscene(root);

  // ── Asset load (idempotent) ──────────────────────────────────────────────
  void loadGameAssets(session.tileMap.tiledMap);

  // ── RAF loop ─────────────────────────────────────────────────────────────
  let lastTime = performance.now();
  let rafId = 0;
  const tick = (): void => {
    const now = performance.now();
    const delta = now - lastTime;
    lastTime = now;
    if (ctx) {
      runGameRenderFrame(ctx, getRenderAssets(), delta, setActiveGate() && intro.isClosed(), now);
    }
    rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);

  // ── Completion polling ───────────────────────────────────────────────────
  const pollId = globalThis.setInterval(() => {
    if (!setActiveGate()) return;
    if (gameState.gameOver && !gameOver) {
      gameOver = true;
      hud.setActive(false);
      closeOverlay();
      overlay = showGameOverOverlay(root, {
        onRestart: handleRestart,
        onQuit: handleQuit,
      });
      return;
    }
    if (gameState.levelComplete && !levelComplete) {
      levelComplete = true;
      hud.setActive(false);
      const stats = {
        seeds: gameState.seeds,
        time: gameState.elapsedSeconds,
        score: gameState.score,
      };
      void bundle.persist(session.id);
      const next = bundle.getNext(session.id);
      closeOverlay();
      // DECISION: the finale replaces the level-complete card rather than
      // stacking on top of it. Showing "Next Level: none / Quit" and then
      // the ending would frame the payoff as a dead end; the ending's own
      // final button returns to the star map.
      if (isFinaleLevelId(session.id)) {
        overlay = startEndingCutscene(root, handleQuit);
        return;
      }
      overlay = showLevelCompleteOverlay(root, {
        seeds: stats.seeds,
        time: stats.time,
        score: stats.score,
        onRestart: handleRestart,
        onQuit: handleQuit,
        ...(next !== null ? { onNextLevel: handleNextLevel } : {}),
      });
    }
  }, POLL_MS);

  // ── Visibility / blur — clear stuck input ────────────────────────────────
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') clearAllInputState();
  };
  document.addEventListener('visibilitychange', onVisibility);

  // ── Pause via Escape ─────────────────────────────────────────────────────
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape' && setActiveGate()) handlePause();
  };
  document.addEventListener('keydown', onKey);

  document.getElementById('app')?.append(root);

  return {
    destroy: (): void => {
      cancelledSaveReconcile = true;
      cancelAnimationFrame(rafId);
      globalThis.clearInterval(pollId);
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('keydown', onKey);
      detachKeyboard();
      touch?.destroy();
      hud.destroy();
      closeOverlay();
      clearGameEventHandlers();
      stopMusic();
      scene.destroy();
      root.remove();
    },
  };
};
